import { z } from "zod";
import {
  Appointment,
  AppointmentStatus,
  AppointmentStatusSchema,
  CreateAppointmentDto,
  RecurrenceFrequencySchema,
  UpdateAppointmentDto,
} from "@myapp/contract";
import {
  APPOINTMENT_DEFAULT_DEPARTURE_MINUTES,
  APPOINTMENT_DEFAULT_TRAVEL_MINUTES,
  APPOINTMENT_DEFAULT_WORK_MINUTES,
} from "../../../entities/appointment";
import { fromDatetimeLocal, toDatetimeLocal } from "../../../shared/helpers/date";
import {
  overrideFields,
  type ScheduleOverrides,
  type ScheduleSlot,
} from "../../../shared/lib/schedule";

/**
 * One form for create and edit. The two differ only in which mapper runs and whether `recurrence`
 * is offered — `POST /appointments` and `PATCH /appointments/:id` take the same business fields.
 *
 * THE TRAP THIS FILE EXISTS TO AVOID: `plannedStart` is the **work** start, and it round-trips
 * through `schedule.plannedWorkStartsAt` — never `schedule.startsAt`, which is the rendered block
 * and begins `plannedTravelMinutes` earlier. Reading the block start back into the form would
 * silently walk every appointment backwards by its travel time on each save.
 *
 * THE SECOND TRAP: the `actual*Duration` fields are only ever sent for a completed appointment.
 * Occupancy is `COALESCE(actual, planned)` per component, so an `actualWorkDuration` on a still
 * PLANNED appointment resizes its calendar block — with a 200 and no error anywhere.
 *
 * That gate reads the **form's** `status`, not the loaded appointment's, because the status is now
 * editable: a manager who picks "Abgeschlossen" gets the Ist-Zeiten fields in the same render and
 * submits status and actuals in one request. Reading the server's status instead would offer the
 * fields one save late, and send nothing the first time.
 *
 * THE THIRD TRAP, and what `toSlot` / `applySlot` exist for: occupancy is `COALESCE(actual, planned)`
 * per component, so a **completed** appointment is drawn from its Ist-Zeiten. The calendar block and
 * the five fields the board reads and writes therefore have to be the Ist ones for as long as the
 * form says the appointment is done — reading `plannedStart` there would make the block jump off the
 * position it was drawn at the moment the form opened, and writing it back would save a move that
 * never becomes visible. `hasActuals` decides the side in exactly one place, here.
 */

/**
 * Actuals are collected once the technician is done, so the form offers them exactly for the two
 * completed states. Single source of truth for both the fields and the update mapper.
 */
export function hasActuals(status?: AppointmentStatus): boolean {
  return status === "COMPLETED" || status === "COMPLETED_WITH_FOLLOW_UP";
}

const recurrenceFormSchema = z.object({
  frequency: RecurrenceFrequencySchema,
  until: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Format TT.MM.JJJJ"),
});

/**
 * German messages on every constraint, including the ones that are hard to reach.
 *
 * Zod's defaults are English ("Number must be greater than or equal to 0"), and this UI is not.
 * The duration fields rarely surface one — `FormInput` maps an empty box to `""` and `z.coerce`
 * turns that into `0` — but a pasted `-30` or `1.5` reaches them, and an untranslated sentence in
 * the middle of a German form is worse than the field it belongs to.
 */
const MINUTES = { invalid: "Ganze Minuten angeben", negative: "Darf nicht negativ sein" } as const;

export const appointmentFormSchema = z
  .object({
    workOrderId: z.string().min(1, "Pflichtfeld"),
    /** Stays nullable so the empty form has a valid default; emptiness is caught below. */
    assignedTechnicianId: z.number().int().positive("Pflichtfeld").nullable(),
    /** `<input type="datetime-local">` value — local wall-clock, no timezone. */
    
    tradeCode: z.coerce.number().int("Ungültiges Gewerk").min(0, "Ungültiges Gewerk"),
    plannedStart: z.string().min(1, "Pflichtfeld"),
    plannedWorkDuration: z.coerce.number().int(MINUTES.invalid).positive("Pflichtfeld"),
    plannedTravelDuration: z.coerce.number().int(MINUTES.invalid).min(0, MINUTES.negative),
    plannedDepartureDuration: z.coerce.number().int(MINUTES.invalid).min(0, MINUTES.negative),
    /**
     * Ist-Zeiten. Always present in the form state so the inputs stay controlled, but only
     * surfaced and only submitted for a completed appointment — see `hasActuals`.
     *
     * `actualStart` is the Ist **work** start, the twin of `plannedStart` and subject to the same
     * trap: it round-trips through `schedule.actualWorkStartsAt`, never through `startsAt`.
     *
     * `actualEnd` is the odd one out: it is **not** part of the occupancy CHECK, so it never moves
     * or resizes the block. It is a reported fact — when the Handwerker signed off — recorded by
     * `PATCH :id/complete` from its own `endTime`, independently of the durations beside it. Two
     * things read it, which is why it matters that a completed Termin has one:
     * `actualWorkStartsAt && !actualEndsAt` is what the board and both worker calendars draw as
     * *In Arbeit*, and the Arbeitszeit-Report reads the column directly.
     */
    actualStart: z.string(),
    actualEnd: z.string(),
    actualWorkDuration: z.coerce.number().int(MINUTES.invalid).min(0, MINUTES.negative),
    actualTravelDuration: z.coerce.number().int(MINUTES.invalid).min(0, MINUTES.negative),
    actualDepartureDuration: z.coerce.number().int(MINUTES.invalid).min(0, MINUTES.negative),
    description: z.string(),
    /**
     * Edit only — a new appointment's status is the backend's to decide (PLANNED, or FOLLOW_UP for
     * a follow-up visit), so create carries the default and never sends it.
     */
    status: AppointmentStatusSchema,
    isFullDay: z.boolean(),
    attachments: z.array(z.string()),
    /** `null` === one-time. Only offered on create; frequency is not patchable. */
    recurrence: recurrenceFormSchema.nullable(),
    /** Edit only, and only meaningful on an appointment that has a `seriesId`. */
    forFullSeries: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.assignedTechnicianId == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Pflichtfeld",
        path: ["assignedTechnicianId"],
      });
    }
    if (data.recurrence && !fromDatetimeLocal(data.plannedStart)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Ungültiges Datum",
        path: ["plannedStart"],
      });
    }
  });

export type AppointmentFormValues = z.infer<typeof appointmentFormSchema>;

/**
 * Ist-Ende as the Ist times imply it: the work start plus the work duration.
 *
 * **Work, not the block** — `actualEndsAt` is when the job was finished, and the Abfahrt is recorded
 * separately and happens after it. `CompleteAppointmentUseCase` collects `endTime` and
 * `actualDepartureDuration` as two independent inputs for exactly that reason.
 *
 * `""` for an incomplete start or an emptied duration, which callers read as "nothing to derive"
 * rather than writing a wrong value. Both in and out are `<input type="datetime-local">` values.
 */
export function deriveActualEnd(actualStart: string, actualWorkDuration: number): string {
  const startIso = fromDatetimeLocal(actualStart);
  if (!startIso || !Number.isFinite(actualWorkDuration)) return "";
  return toDatetimeLocal(
    new Date(new Date(startIso).getTime() + actualWorkDuration * 60_000),
  );
}

export function getDefaultValues(
  workOrderId = "",
  tradeCode = 0,
): AppointmentFormValues {
  return {
    workOrderId,
    assignedTechnicianId: null,
    plannedStart: "",
    tradeCode,
    plannedWorkDuration: APPOINTMENT_DEFAULT_WORK_MINUTES,
    plannedTravelDuration: APPOINTMENT_DEFAULT_TRAVEL_MINUTES,
    plannedDepartureDuration: APPOINTMENT_DEFAULT_DEPARTURE_MINUTES,
    actualStart: "",
    actualEnd: "",
    actualWorkDuration: 0,
    actualTravelDuration: 0,
    actualDepartureDuration: 0,
    description: "",
    // Never submitted on create; here only so the select stays controlled if the form is reused.
    status: "PLANNED",
    isFullDay: false,
    attachments: [],
    recurrence: null,
    forFullSeries: false,
  };
}

/** Everything `toSlot` and `applySlot` read. `status` decides which side of the COALESCE they use. */
export type SlotValues = Pick<
  AppointmentFormValues,
  | "assignedTechnicianId"
  | "plannedStart"
  | "plannedTravelDuration"
  | "plannedWorkDuration"
  | "plannedDepartureDuration"
  | "actualStart"
  | "actualTravelDuration"
  | "actualWorkDuration"
  | "actualDepartureDuration"
> & { status?: AppointmentStatus };

/**
 * The four scheduling values as a `ScheduleSlot` — **what the calendar draws** while the form is
 * open, which for a completed appointment is its Ist-Zeiten and otherwise its Plan-Zeiten.
 *
 * The returned keys are named after the planned fields because they are the RHF field names the
 * board never inspects; see the note on `ScheduleSlot` itself. What they carry is the side
 * `hasActuals` selects.
 *
 * `null` means "there is nothing to draw yet", not "clear the block": no Handwerker chosen, a start
 * still being typed, or a duration input the user has just emptied (`valueAsNumber` hands back
 * `NaN`). The board keeps showing the last good position in that case, which is why this must not
 * fall back to zeroes — a `NaN` smuggled through would place the block at 06:00.
 *
 * No mapping happens here: the start stays the datetime-local **work** start, exactly as the board's
 * `slotToDraft` expects it.
 */
export function toSlot(values: SlotValues): ScheduleSlot | null {
  const actuals = hasActuals(values.status);

  const start = actuals ? values.actualStart : values.plannedStart;
  const travel = actuals ? values.actualTravelDuration : values.plannedTravelDuration;
  const work = actuals ? values.actualWorkDuration : values.plannedWorkDuration;
  const departure = actuals
    ? values.actualDepartureDuration
    : values.plannedDepartureDuration;

  if (values.assignedTechnicianId == null) return null;
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(start)) return null;
  if (![travel, work, departure].every(Number.isFinite)) return null;

  return {
    assignedTechnicianId: values.assignedTechnicianId,
    plannedStart: start,
    plannedTravelDuration: travel,
    plannedWorkDuration: work,
    plannedDepartureDuration: departure,
  };
}

/**
 * The inverse of `toSlot`: a slot chosen elsewhere — the board — written onto whichever side this
 * form's status puts it on.
 *
 * Used by the seeding `reset`, where the picked slot has to win over the loaded appointment. A plain
 * spread of the slot cannot do it any more: its keys are the *planned* field names, so on a
 * completed appointment it would fill the Plan-Zeiten with a position the block does not use and
 * leave the Ist-Zeiten — the ones actually drawn — untouched.
 */
export function applySlot(
  values: AppointmentFormValues,
  slot?: ScheduleSlot,
): AppointmentFormValues {
  if (!slot) return values;

  const scheduling = hasActuals(values.status)
    ? {
        actualStart: slot.plannedStart,
        actualTravelDuration: slot.plannedTravelDuration,
        actualWorkDuration: slot.plannedWorkDuration,
        actualDepartureDuration: slot.plannedDepartureDuration,
      }
    : {
        plannedStart: slot.plannedStart,
        plannedTravelDuration: slot.plannedTravelDuration,
        plannedWorkDuration: slot.plannedWorkDuration,
        plannedDepartureDuration: slot.plannedDepartureDuration,
      };

  return {
    ...values,
    assignedTechnicianId: slot.assignedTechnicianId,
    ...scheduling,
  };
}

export function toCreateDto(
  values: AppointmentFormValues,
  overrides: ScheduleOverrides = {},
): CreateAppointmentDto {
  return {
    workOrderId: values.workOrderId,
    // Non-null by the time this runs — `superRefine` blocks submit otherwise.
    assignedTechnicianId: values.assignedTechnicianId as number,
    plannedStart: fromDatetimeLocal(values.plannedStart) as string,
    tradeCode: values.tradeCode,
    plannedWorkDuration: values.plannedWorkDuration,
    plannedTravelDuration: values.plannedTravelDuration,
    plannedDepartureDuration: values.plannedDepartureDuration,
    attachments: values.attachments.length ? values.attachments : null,
    description: values.description || null,
    isFullDay: values.isFullDay,
    recurrence: values.recurrence ?? undefined,
    ...overrideFields(overrides),
  };
}

/**
 * `recurrence` is deliberately absent: `UpdateAppointmentDtoSchema` does not accept it. Changing a
 * frequency would mean re-reserving every future occurrence, so the API refuses — the horizon moves
 * through `PATCH /appointments/series/:seriesId` instead.
 *
 * With `forFullSeries`, `plannedStart` is treated by the backend as a **delta**: it moves this
 * appointment to the given time and shifts every later occurrence by the same offset. The client
 * still sends the plain absolute time — no offset math here.
 *
 * The `actual*` keys are omitted entirely unless the appointment is completed. The DTO is
 * `.partial()`, so omitting them leaves the stored actuals alone; sending them on a PLANNED
 * appointment would move its block, because occupancy prefers actuals over planned.
 *
 * `actualStart` rides with the three durations because it is what *anchors* them: a completed
 * appointment is drawn from `actualWorkStartsAt ?? plannedWorkStartsAt`, so a move that wrote only
 * the durations would resize the block without relocating it. `actualEnd` moves nothing — it is not
 * in the occupancy CHECK — but it is what *In Arbeit* is read from, so a completed Termin that is
 * given an Ist start has to be given an Ist end in the same request or it starts rendering as still
 * running. Both are dropped rather than sent as `null` for an unparseable value: the DTO types them
 * `z.string().datetime()`, not nullable.
 *
 * The planned keys keep being sent unchanged on a completed appointment. That is deliberate: they
 * carry what was *planned*, the drag never touched them, and re-sending the loaded values is what
 * preserves the Plan/Ist comparison.
 *
 * `status` is sent unconditionally, and the API ignores it when it matches what is stored. Setting
 * it to a completed state carries the same WorkOrder bump as `PATCH :id/complete` and requires a
 * manager role, so a 403 here is about the status, not the time.
 *
 * The overrides behave exactly as in `toCreateDto`: set only after the user confirmed the
 * matching dialog.
 */
export function toUpdateDto(
  values: AppointmentFormValues,
  options: ScheduleOverrides & { hasSeries: boolean },
): UpdateAppointmentDto {
  return {
    assignedTechnicianId: values.assignedTechnicianId as number,
    plannedStart: fromDatetimeLocal(values.plannedStart) as string,
    tradeCode: values.tradeCode,
    plannedWorkDuration: values.plannedWorkDuration,
    plannedTravelDuration: values.plannedTravelDuration,
    plannedDepartureDuration: values.plannedDepartureDuration,
    attachments: values.attachments.length ? values.attachments : null,
    description: values.description || null,
    isFullDay: values.isFullDay,
    status: values.status,
    ...(hasActuals(values.status)
      ? {
          ...(fromDatetimeLocal(values.actualStart)
            ? { actualStart: fromDatetimeLocal(values.actualStart) as string }
            : {}),
          ...(fromDatetimeLocal(values.actualEnd)
            ? { actualEnd: fromDatetimeLocal(values.actualEnd) as string }
            : {}),
          actualWorkDuration: values.actualWorkDuration,
          actualTravelDuration: values.actualTravelDuration,
          actualDepartureDuration: values.actualDepartureDuration,
        }
      : {}),
    ...(options.hasSeries ? { forFullSeries: values.forFullSeries } : {}),
    ...overrideFields(options),
  };
}

export function fromAppointment(a: Appointment): AppointmentFormValues {
  const actualStart = toDatetimeLocal(
    a.schedule.actualWorkStartsAt ?? a.schedule.plannedWorkStartsAt,
  );
  const actualWorkDuration = a.schedule.actualWorkMinutes ?? a.schedule.plannedWorkMinutes;

  return {
    workOrderId: a.workOrderId,
    assignedTechnicianId: a.schedule.technicianId,
    // plannedWorkStartsAt, NOT startsAt — see the note at the top of this file.
    plannedStart: toDatetimeLocal(a.schedule.plannedWorkStartsAt),
    tradeCode: a.tradeCode,
    plannedWorkDuration: a.schedule.plannedWorkMinutes,
    plannedTravelDuration: a.schedule.plannedTravelMinutes,
    plannedDepartureDuration: a.schedule.plannedDepartureMinutes,
    // Actuals are nullable until the technician reports them; the planned value is the sensible
    // starting point for a manager correcting them, and never a silent 0. `actualStart` follows the
    // same fallback: a completed appointment that was never *started* has no Ist start, and opening
    // the form on a blank one would publish nothing to the calendar at all.
    actualStart,
    // The stored sign-off wins; a completed Termin that never carried one — the technician-driven
    // `complete` route is the only thing that writes it — opens on the value its own Ist times
    // imply, rather than on a blank that would leave it reading as *In Arbeit* forever.
    actualEnd:
      toDatetimeLocal(a.schedule.actualEndsAt) ||
      deriveActualEnd(actualStart, actualWorkDuration),
    actualWorkDuration,
    actualTravelDuration:
      a.schedule.actualTravelMinutes ?? a.schedule.plannedTravelMinutes,
    actualDepartureDuration:
      a.schedule.actualDepartureMinutes ?? a.schedule.plannedDepartureMinutes,
    description: a.description ?? "",
    status: a.status,
    isFullDay: a.schedule.allDay,
    attachments: a.attachments ?? [],
    recurrence: null,
    forFullSeries: false,
  };
}
